import { DashboardModal } from "@dashboard/components/Modal";
import { buttonMessages } from "@dashboard/intl";
import { colorTransition } from "@dashboard/utils/motion";
import { Box, Modal, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AnimatedHeight } from "./AnimatedHeight";

interface HomeAnalyticsCardProps {
  testId?: string;
  title: string;
  children?: React.ReactNode;
  /** Rendered under the "Today" caption. */
  secondaryLine?: React.ReactNode;
  /** When given, the card becomes a button that opens this in a dialog. */
  detail?: React.ReactNode;
  /** Controlled: the owner keeps this in the URL so it survives navigation. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const TodayCaption = () => (
  <Text size="medium" variant="caption" color="default1">
    <FormattedMessage id="zWgbGg" defaultMessage="Today" />
  </Text>
);

// Nothing interactive belongs in here: with a detail view this renders inside
// a <button>, and a nested link would both navigate and open the dialog.
const CardHeader = ({
  title,
  secondaryLine,
  children,
}: Pick<HomeAnalyticsCardProps, "title" | "secondaryLine" | "children">) => (
  <>
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Text size="large" variant="body">
        {title}
      </Text>
      <TodayCaption />
      {secondaryLine}
    </Box>
    {/* Not a heading: with a detail view this sits inside a <button>, and a
        heading there would land heading navigation on a control. The dialog
        title carries the heading instead. */}
    <Text as="span" display="block" variant="heading">
      {children}
    </Text>
  </>
);

export const HomeAnalyticsCard = ({
  children,
  title,
  testId,
  secondaryLine,
  detail,
  open = false,
  onOpenChange,
}: HomeAnalyticsCardProps) => {
  const intl = useIntl();

  if (!detail) {
    return (
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        paddingX={3}
        paddingY={5}
        display="flex"
        justifyContent="space-between"
        data-test-id={testId}
      >
        <CardHeader title={title} secondaryLine={secondaryLine}>
          {children}
        </CardHeader>
      </Box>
    );
  }

  // The card itself is the dialog trigger: a native button gives Enter/Space
  // activation, and Radix returns focus to it when the dialog closes. The
  // detail used to expand in place, inside one half of the card grid, which
  // left the order rows no room; a dialog can be as wide as the page.
  return (
    <DashboardModal open={open} onChange={onOpenChange}>
      <Modal.Trigger>
        <Box
          as="button"
          type="button"
          borderWidth={1}
          borderStyle="solid"
          borderColor={{ default: "default1", hover: "default1Hovered" }}
          borderRadius={3}
          backgroundColor={{ default: "default1", hover: "default1Hovered" }}
          boxShadow={{ default: "none", focusVisible: "defaultFocused" }}
          __transition={colorTransition("background-color", "border-color")}
          paddingX={3}
          paddingY={5}
          display="flex"
          justifyContent="space-between"
          gap={3}
          width="100%"
          // Grid items are min-width:auto, so without this the card grows to
          // fit its longest line instead of letting it wrap.
          minWidth={0}
          textAlign="left"
          cursor="pointer"
          data-test-id={testId}
        >
          <CardHeader title={title} secondaryLine={secondaryLine}>
            {children}
          </CardHeader>
        </Box>
      </Modal.Trigger>
      <DashboardModal.Content
        __width="min(880px, calc(100vw - 32px))"
        __maxHeight="90vh"
        display="flex"
        flexDirection="column"
        gap={0}
        paddingX={0}
        paddingBottom={0}
        data-test-id={`${testId}-dialog`}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={3}
          paddingX={6}
          paddingBottom={4}
          flexShrink="0"
        >
          <Box display="flex" flexDirection="column" gap={0.5}>
            <DashboardModal.Title as="h2">{title}</DashboardModal.Title>
            <TodayCaption />
          </Box>
          <DashboardModal.Close
            aria-label={intl.formatMessage(buttonMessages.close)}
            data-test-id="close-card-dialog"
          />
        </Box>
        {/* The rows scroll; the title and close control stay put. Polite live
            region: the out-of-stock rows arrive after the dialog has opened. */}
        <AnimatedHeight>{detail}</AnimatedHeight>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
